import {WithStyledProps} from '@coolcolduk/ui-mui';
import {Article, CheckCircle, HourglassEmpty, PauseCircle, Pending, PlayArrow} from '@mui/icons-material';
import {Box, Grid, IconButton, Tooltip, Typography, styled} from '@mui/material';
import dayjs from 'dayjs';
import {useState} from 'react';
import {env} from '../../constants';
import {firebaseAuth} from '../../setup/firebase';
import getAxios from '../../util/axios/getAxios';
import {QueueJob, QueueJobStatus} from '../../__generated__/resolvers-types';
import JobListItemLog from './JobListItemLog';

export interface JobListItemProps {
  job: QueueJob;
}

function JobListItemInternal({className, job}: WithStyledProps<JobListItemProps>) {
  const [jobLogOpen, setJobLogOpen] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);

  const handleTriggerQueueJobRequest = async () => {
    const token = await firebaseAuth.currentUser?.getIdToken();
    setTriggerLoading(true);
    try {
      const params = job.id ? {params: {id: job.id}} : undefined;
      await getAxios('queueJobRequest', {token}).get('', params);
    } finally {
      setTriggerLoading(false);
    }
  };

  const items =
    job.etsyBulkCreateCsv?.items ||
    job.etsyBulkCreateGdriveFile?.items ||
    job.etsyBulkCreateGdriveFolder?.items ||
    job.etsyBulkUpdateCsv?.items ||
    job.imageToImageGeneration?.items ||
    [];
  const successCount = items.filter((item) => item.status === 'SUCCESS').length;
  const errorCount = items.filter((item) => item.status === 'ERROR').length;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case QueueJobStatus.COMPLETE:
        return <CheckCircle color="success" />;
      case QueueJobStatus.QUEUE:
        return <HourglassEmpty color="action" />;
      case QueueJobStatus.PAUSED:
        return <PauseCircle color="warning" />;
      default:
        return <Pending color="action" />;
    }
  };

  return (
    <Grid
      className={className}
      container
      justifyContent="space-between"
      alignItems="center"
      sx={{
        '--Grid-borderWidth': '1px',
        borderTop: 'var(--Grid-borderWidth) solid',
        borderBottom: 'var(--Grid-borderWidth) solid',
        borderColor: 'divider',
      }}
      spacing={1}
      wrap="wrap"
    >
      <Grid>
        <Typography variant="body2">ID: {job.id}</Typography>
      </Grid>
      <Grid>
        <Typography variant="body2" color="textSecondary" noWrap>
          {dayjs(job.creationTimestamp * 1000).format('MMM D, h:mm A')}
        </Typography>
      </Grid>
      <Grid>
        <Tooltip title={job.status}>
          <Box display="flex" alignItems="center">
            Status: {getStatusIcon(job.status)}
          </Box>
        </Tooltip>
      </Grid>
      <Grid>
        <Typography variant="body2" sx={{flex: '0 0 100px'}}>
          Total: {items.length}
        </Typography>
      </Grid>
      <Grid>
        <Typography variant="body2" sx={{flex: '0 0 80px'}}>
          ✓ {successCount}
        </Typography>
      </Grid>
      <Grid>
        <Typography variant="body2" color="error" sx={{flex: '0 0 80px'}}>
          ✗ {errorCount}
        </Typography>
      </Grid>
      <Grid>
        {env.isDevelopment && (
          <Tooltip title="Trigger queueJobRequest (dev)">
            <span>
              <IconButton
                onClick={handleTriggerQueueJobRequest}
                disabled={triggerLoading}
                size="small"
                aria-label="Trigger queue job"
              >
                <PlayArrow />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <IconButton
          onClick={() => setJobLogOpen(true)}
          title="View Job Error Log"
          sx={{visibility: errorCount > 0 ? 'visible' : 'hidden'}}
        >
          <Article />
        </IconButton>
        <JobListItemLog open={jobLogOpen} onClose={() => setJobLogOpen(false)} job={job} />
      </Grid>
    </Grid>
  );
}

export const JobListItem = styled(JobListItemInternal, {name: 'CcJobListItem'})<JobListItemProps>();
